import { useEffect, useState } from 'react';
import { useUserGroups } from '@/hooks/queries/useGroups';
import type { Group } from '@/types/domain.types';
import { logger } from '@/lib/logger';

const DB_NAME = 'ExpensesDB';
const DB_VERSION = 1;
const GROUPS_STORE_NAME = 'groups';
const CACHE_DURATION = 1000 * 60 * 60 * 24 * 7; // 7 days - groups rarely change

interface GroupsCache {
  id: string;
  groups: Group[];
  timestamp: number;
}

/**
 * Initialize IndexedDB
 */
async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create groups store if it doesn't exist
      if (!db.objectStoreNames.contains(GROUPS_STORE_NAME)) {
        db.createObjectStore(GROUPS_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Load cached groups from IndexedDB
 */
async function loadCachedGroups(): Promise<GroupsCache | null> {
  try {
    const db = await initDB();
    const transaction = db.transaction([GROUPS_STORE_NAME], 'readonly');
    const store = transaction.objectStore(GROUPS_STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get('cached-groups');

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(new Error('Failed to load cached groups'));
      };
    });
  } catch (error) {
    logger.error('[useOfflineGroupsReadOnly] Error loading from IndexedDB:', error);
    return null;
  }
}

/**
 * Save groups to IndexedDB cache
 */
async function saveCachedGroups(groups: Group[]): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([GROUPS_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(GROUPS_STORE_NAME);

    const cache: GroupsCache = {
      id: 'cached-groups',
      groups,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cache);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to save cached groups'));
      };
    });
  } catch (error) {
    logger.error('[useOfflineGroupsReadOnly] Error saving to IndexedDB:', error);
  }
}

/**
 * Lightweight read-only offline groups hook for group selection
 * - Uses the regular useUserGroups when online
 * - Falls back to cached data when offline
 * - Automatically updates cache when new data is fetched
 * - Uses IndexedDB for better storage capabilities
 */
export function useOfflineGroupsReadOnly() {
  const [cachedGroups, setCachedGroups] = useState<Group[]>([]);
  const [isLoadingCache, setIsLoadingCache] = useState(true);

  // Use the regular groups query
  const {
    data: serverGroups,
    isLoading: isLoadingServer,
    error: serverError,
    refetch,
  } = useUserGroups();

  // Load cached groups on mount
  useEffect(() => {
    const loadCache = async () => {
      try {
        const cached = await loadCachedGroups();

        if (cached) {
          const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;

          if (cached.groups && cached.groups.length > 0) {
            setCachedGroups(cached.groups);
            logger.debug('[useOfflineGroupsReadOnly] Loaded cached groups from IndexedDB:', {
              count: cached.groups.length,
              age: Math.round((Date.now() - cached.timestamp) / 1000 / 60) + ' minutes',
              expired: isExpired,
            });
          } else {
            logger.debug('[useOfflineGroupsReadOnly] No cached groups found in IndexedDB');
          }
        }
      } catch (error) {
        logger.error('[useOfflineGroupsReadOnly] Error loading cache:', error);
      } finally {
        setIsLoadingCache(false);
      }
    };

    loadCache();
  }, []);

  // Update cache when server data changes
  useEffect(() => {
    if (serverGroups && serverGroups.length > 0) {
      const updateCache = async () => {
        try {
          // Convert null to undefined for pinned_categories to match Group type
          const normalizedGroups = serverGroups.map(group => ({
            ...group,
            pinned_categories: group.pinned_categories === null ? undefined : group.pinned_categories
          }));
          await saveCachedGroups(normalizedGroups);
          setCachedGroups(normalizedGroups);
          logger.debug('[useOfflineGroupsReadOnly] Updated IndexedDB cache with server data:', {
            count: normalizedGroups.length,
          });
        } catch (error) {
          logger.error('[useOfflineGroupsReadOnly] Error updating cache:', error);
        }
      };

      updateCache();
    }
  }, [serverGroups]);

  // Detect offline status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      logger.debug('[useOfflineGroupsReadOnly] Came online, will use fresh data');
    };

    const handleOffline = () => {
      setIsOnline(false);
      logger.debug('[useOfflineGroupsReadOnly] Went offline, using cached data');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Determine which data to use
  const groups = serverGroups || cachedGroups;
  const isLoading = isLoadingCache || (isLoadingServer && !cachedGroups.length);
  const isUsingCache = !isOnline && cachedGroups.length > 0;

  return {
    groups,
    isLoading,
    isOnline,
    isUsingCache,
    isError: !!serverError && !cachedGroups.length,
    error: serverError,
    refetch,
    hasCachedData: cachedGroups.length > 0,
  };
}